import { Button } from "@/components/ui/button";
import { Home, Users, User, LogIn, LogOut, Bell, Plus, Settings, MessageCircle, HelpCircle, Share2, Heart, UserCheck } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { CreatePostDialog } from "@/components/CreatePostDialog";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setIsLoggedIn(!!data.session);
      
      // Check if user is admin
      if (data.session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('stats')
          .eq('id', data.session.user.id)
          .single();
          
        setIsAdmin((profile?.stats as any)?.role === 'admin');
      }
    };
    
    checkSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setIsLoggedIn(!!session);
      
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('stats')
          .eq('id', session.user.id)
          .single();
          
        setIsAdmin((profile?.stats as any)?.role === 'admin');
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);
  
  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error logging out",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Logged out successfully",
      description: "See you soon! 👋",
    });
    navigate('/auth');
  };

  const handleLogin = () => {
    navigate('/auth');
  };

  const handleHomeClick = () => {
    // Navigate to home and reset any category filters
    navigate('/', { replace: true });
    // Force a complete refresh to reset all filters
    window.location.href = '/';
  };

  const isNotificationsRoute = location.pathname === '/notifications';

  return (
    <div className="flex flex-col min-h-[100dvh] bg-gradient-to-b from-pink-50 to-purple-50 dark:from-pink-900 dark:to-purple-800">
      <header className="border-b bg-white/90 backdrop-blur-md dark:bg-gray-900/90 sticky top-0 z-10 shadow-md">
        <div className="w-full max-w-full mx-auto px-4 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={handleHomeClick}
            role="button"
            tabIndex={0}
          >
            <img 
              src="/lovable-uploads/e9ee540f-ae0a-4d24-b1a2-69eb3153196d.png" 
              alt="Positivio" 
              className="h-12"
            />
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 italic">
              See the good in people
            </span>
          </div>


          <Button
            variant="outline"
            size="sm"
            className="gap-2 bg-gradient-to-r from-pink-50 to-purple-50 hover:from-pink-100 hover:to-purple-100 dark:from-gray-800 dark:to-gray-700 rounded-full border-0 shadow-sm"
            onClick={isLoggedIn ? handleLogout : handleLogin}
          >
            {isLoggedIn ? (
              <>
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                <span>Login</span>
              </>
            )}
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-8 overflow-y-auto">
        {children}
      </main>

      <nav className="sticky bottom-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-t py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] z-50">
        <div className="w-full max-w-full mx-auto px-4">
          <div className="flex justify-around items-center">
            <Button 
              variant="ghost" 
              size="icon" 
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-pink-500 dark:text-gray-300 dark:hover:text-pink-400"
              onClick={handleHomeClick}
            >
              <Home className="w-6 h-6" />
              <span className="text-xs">Home</span>
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-purple-500 dark:text-gray-300 dark:hover:text-purple-400"
              onClick={() => navigate('/friends')}
            >
              <UserCheck className="w-6 h-6" />
              <span className="text-xs">Friends</span>
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="flex flex-col items-center justify-center relative h-20"
              onClick={() => setShowCreateDialog(true)}
            >
              <div className="absolute -top-6 w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center shadow-lg">
                <Plus className="w-8 h-8 text-white" />
              </div>
              <span className="text-xs mt-12 text-gray-700 dark:text-gray-300 font-medium">Create</span>
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-violet-500 dark:text-gray-300 dark:hover:text-violet-400"
              onClick={() => navigate('/groups')}
            >
              <Users className="w-6 h-6" />
              <span className="text-xs">Groups</span>
            </Button>
            
            {isAdmin ? (
              <Button 
                variant="ghost" 
                size="icon" 
                className="flex flex-col items-center gap-1 text-gray-600 hover:text-amber-500 dark:text-gray-300 dark:hover:text-amber-400"
                onClick={() => navigate('/admin')}
              >
                <Settings className="w-6 h-6" />
                <span className="text-xs">Admin</span>
              </Button>
            ) : (
              <Button 
                variant="ghost" 
                size="icon" 
                className="flex flex-col items-center gap-1 text-gray-600 hover:text-pink-500 dark:text-gray-300 dark:hover:text-pink-400"
                onClick={() => navigate('/profile')}
              >
                <User className="w-6 h-6" />
                <span className="text-xs">Profile</span>
              </Button>
            )}
          </div>
        </div>
      </nav>

      <CreatePostDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog} 
      />
    </div>
  );
};
